<template>
  <div class="max-w-6xl mx-auto flex flex-col lg:flex-row gap-6 items-start">

    <!-- Left column: info + participation notice (sticky on large screens) -->
    <div class="flex flex-col gap-4 w-full lg:w-[250px] shrink-0 lg:sticky lg:top-6 lg:self-start">
      <CompetitionInfoPanel
        :competition="competition"
        :universe-stocks-count="universeStocks.length"
      />
    </div>

    <!-- Right column: main content + full standings -->
    <div class="flex flex-col gap-6 flex-1 min-w-0 w-full">

      <!-- Main content card -->
      <div class="card shadow-md bg-base-100 rounded-xl w-full">
        <div class="card-body p-4 sm:p-5">
          <div class="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-6 items-stretch">

            <!-- Chart -->
            <div class="min-w-0 h-[420px] lg:h-full">
              <apexchart
                class="h-full"
                type="line"
                height="100%"
                :options="chartOptions"
                :series="chartSeries"
              />
            </div>

            <!-- Leaderboard -->
            <LeaderboardPanel
              :tab="rightPanelTab"
              :top-players="topLeaderboardEntries"
              :bottom-player="bottomRankedEntry"
              :missing-players-count="missingPlayersCount"
              :top-stocks="topUniverseRankedEntries"
              :bottom-stock="bottomUniverseEntry"
              :missing-stocks-count="missingUniverseCount"
              :universe-stocks-total="universeStocks.length"
              :hovered-player-name="hoveredPlayerName"
              :focused-player-name="focusedPlayerName"
              :hovered-stock-symbol="hoveredStockSymbol"
              :focused-stock-symbol="focusedStockSymbol"
              @tab-change="handleTabChange"
              @player-hover="handlePlayerHover"
              @player-leave="clearPlayerHover"
              @player-click="handlePlayerClick"
              @stock-hover="handleStockHover"
              @stock-leave="clearStockHover"
              @stock-click="handleStockClick"
            />
          </div>
        </div>
      </div>

      <!-- Full standings table -->
      <FullStandingsTable :rows="fullTableRows" @expand="showAllPlayers = true" />

    </div><!-- end right column -->

    <!-- Portfolio tooltip -->
    <div
      v-if="rightPanelTab === 'players' && hoveredLeaderboardEntry"
      class="fixed z-50 pointer-events-none"
      :style="tooltipStyle"
      :data-placement="tooltipPlacement"
    >
      <div class="w-[22rem] rounded-xl border border-base-300/60 bg-base-100/95 backdrop-blur-sm shadow-xl p-3">
        <div class="text-xs font-semibold text-base-content/70 mb-2">
          {{ hoveredLeaderboardEntry.display_name }} portfolio
        </div>
        <div class="w-full h-56 rounded-lg overflow-hidden border border-base-300/50">
          <MiniPortfolioTreemap
            :stocks="hoveredLeaderboardEntry.stocks"
            :show-labels="true"
            fill
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { useFictionalDataDisclaimer } from '../composables/useFictionalDataDisclaimer.js'
import VueApexCharts from 'vue3-apexcharts'
import competition from '../data/competition.json'
import MiniPortfolioTreemap from '../components/MiniPortfolioTreemap.vue'
import CompetitionInfoPanel from '../components/CompetitionInfoPanel.vue'
import LeaderboardPanel from '../components/LeaderboardPanel.vue'
import FullStandingsTable from '../components/FullStandingsTable.vue'
import { useCompetitionDates } from '../composables/useCompetitionDates.js'
import { useTradingChart } from '../composables/useTradingChart.js'

const apexchart = VueApexCharts

// Theme tracking
const themeVersion = ref(0)
let observer = null
const { checkAndOpenForCompetition } = useFictionalDataDisclaimer()

onMounted(() => {
  checkAndOpenForCompetition()
  observer = new MutationObserver(() => { themeVersion.value++ })
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
})
onUnmounted(() => observer?.disconnect())

const isDark = computed(() => {
  void themeVersion.value
  return document.documentElement.getAttribute('data-theme') === 'sj-dark'
})

// Interaction state
const rightPanelTab = ref('players')
const hoveredPlayerName = ref(null)
const focusedPlayerName = ref(null)
const hoveredStockSymbol = ref(null)
const focusedStockSymbol = ref(null)
const tooltipX = ref(0)
const tooltipY = ref(0)
const tooltipPlacement = ref('bottom-start')

watch(rightPanelTab, () => {
  focusedPlayerName.value = null
  hoveredPlayerName.value = null
  focusedStockSymbol.value = null
  hoveredStockSymbol.value = null
})

// ── Static data ────────────────────────────────────────────────────────────────

const LEADER_TARGET_RETURNS = {
  1: 22.0,
  2: 21.1,
  3: 20.2,
  4: 17.9,
  5: 16.1,
}

const OVERLAP_STOCKS_BY_RANK = {
  1: [
    { symbol: 'NVDA', weight: 28, return_pct: 40.0 },
    { symbol: 'MSFT', weight: 20, return_pct: 30.0 },
    { symbol: 'AAPL', weight: 16, return_pct: 20.0 },
    { symbol: 'GOOGL', weight: 14, return_pct: 14.0 },
    { symbol: 'META', weight: 12, return_pct: 23.0 },
    { symbol: 'AVGO', weight: 10, return_pct: 21.0 },
  ],
  2: [
    { symbol: 'MSFT', weight: 24, return_pct: 30.0 },
    { symbol: 'NVDA', weight: 20, return_pct: 40.0 },
    { symbol: 'AAPL', weight: 20, return_pct: 20.0 },
    { symbol: 'GOOGL', weight: 16, return_pct: 14.0 },
    { symbol: 'META', weight: 10, return_pct: 23.0 },
    { symbol: 'AMZN', weight: 10, return_pct: 8.0 },
  ],
  3: [
    { symbol: 'META', weight: 26, return_pct: 23.0 },
    { symbol: 'NVDA', weight: 18, return_pct: 40.0 },
    { symbol: 'GOOGL', weight: 18, return_pct: 14.0 },
    { symbol: 'MSFT', weight: 14, return_pct: 30.0 },
    { symbol: 'AMZN', weight: 14, return_pct: 8.0 },
    { symbol: 'AAPL', weight: 10, return_pct: 20.0 },
  ],
  4: [
    { symbol: 'TSLA', weight: 24, return_pct: 16.0 },
    { symbol: 'NVDA', weight: 18, return_pct: 40.0 },
    { symbol: 'AAPL', weight: 18, return_pct: 20.0 },
    { symbol: 'AMZN', weight: 16, return_pct: 8.0 },
    { symbol: 'MSFT', weight: 14, return_pct: 30.0 },
    { symbol: 'GOOGL', weight: 10, return_pct: 14.0 },
  ],
  5: [
    { symbol: 'AMZN', weight: 25, return_pct: 8.0 },
    { symbol: 'AAPL', weight: 20, return_pct: 20.0 },
    { symbol: 'MSFT', weight: 18, return_pct: 30.0 },
    { symbol: 'NVDA', weight: 16, return_pct: 40.0 },
    { symbol: 'GOOGL', weight: 11, return_pct: 14.0 },
    { symbol: 'META', weight: 10, return_pct: 23.0 },
  ],
  6: [
    { symbol: 'AAPL', weight: 30, return_pct: 20.0 },
    { symbol: 'MSFT', weight: 25, return_pct: 30.0 },
    { symbol: 'GOOGL', weight: 20, return_pct: 14.0 },
    { symbol: 'AMZN', weight: 15, return_pct: 8.0 },
    { symbol: 'META', weight: 10, return_pct: 23.0 },
  ],
  7: [
    { symbol: 'MSFT', weight: 22, return_pct: 30.0 },
    { symbol: 'AAPL', weight: 20, return_pct: 20.0 },
    { symbol: 'V', weight: 18, return_pct: 2.0 },
    { symbol: 'JPM', weight: 16, return_pct: 2.0 },
    { symbol: 'GOOGL', weight: 14, return_pct: 14.0 },
    { symbol: 'AMZN', weight: 10, return_pct: 8.0 },
  ],
  8: [
    { symbol: 'COIN', weight: 24, return_pct: -6.0 },
    { symbol: 'MSTR', weight: 18, return_pct: -5.0 },
    { symbol: 'NVDA', weight: 18, return_pct: 40.0 },
    { symbol: 'TSLA', weight: 14, return_pct: 16.0 },
    { symbol: 'AMZN', weight: 14, return_pct: 8.0 },
    { symbol: 'AAPL', weight: 12, return_pct: 20.0 },
  ],
  23: [
    { symbol: 'INTC', weight: 26, return_pct: -16.0 },
    { symbol: 'PYPL', weight: 16, return_pct: -5.2 },
    { symbol: 'AAPL', weight: 16, return_pct: 20.0 },
    { symbol: 'MSFT', weight: 16, return_pct: 30.0 },
    { symbol: 'GOOGL', weight: 14, return_pct: 14.0 },
    { symbol: 'AMZN', weight: 12, return_pct: 8.0 },
  ],
}

const FILLER_PLAYERS = [
  { display_name: 'DiverseDiana',   position_change:  1, total_return: -4.1, daily_avg_return: -0.17, stocks: [{ symbol: 'AAPL', weight: 60, return_pct: 20.0 }, { symbol: 'MSFT', weight: 40, return_pct: 30.0 }] },
  { display_name: 'MacroMarcus',    position_change:  0, total_return: -4.3, daily_avg_return: -0.18, stocks: [{ symbol: 'GOOGL', weight: 60, return_pct: 14.0 }, { symbol: 'AMZN', weight: 40, return_pct: 8.0 }] },
  { display_name: 'SafeHarbour',    position_change: -1, total_return: -4.6, daily_avg_return: -0.19, stocks: [{ symbol: 'BRK.B', weight: 70, return_pct: 3.0 }, { symbol: 'V', weight: 30, return_pct: 2.0 }] },
  { display_name: 'RiskReward',     position_change:  2, total_return: -4.9, daily_avg_return: -0.20, stocks: [{ symbol: 'TSLA', weight: 60, return_pct: 16.0 }, { symbol: 'NVDA', weight: 40, return_pct: 40.0 }] },
  { display_name: 'PatientPaula',   position_change:  0, total_return: -5.1, daily_avg_return: -0.21, stocks: [{ symbol: 'JNJ', weight: 50, return_pct: -2.0 }, { symbol: 'PG', weight: 50, return_pct: 1.0 }] },
  { display_name: 'QuarterlyQuinn', position_change: -2, total_return: -5.4, daily_avg_return: -0.23, stocks: [{ symbol: 'XOM', weight: 50, return_pct: -3.0 }, { symbol: 'CVX', weight: 50, return_pct: -2.0 }] },
  { display_name: 'ETFEvan',        position_change:  1, total_return: -5.7, daily_avg_return: -0.24, stocks: [{ symbol: 'SPY', weight: 100, return_pct: 4.0 }] },
  { display_name: 'OilBaron',       position_change:  0, total_return: -6.0, daily_avg_return: -0.25, stocks: [{ symbol: 'OXY', weight: 60, return_pct: -5.0 }, { symbol: 'XOM', weight: 40, return_pct: -3.0 }] },
  { display_name: 'MidCapMolly',    position_change: -1, total_return: -6.2, daily_avg_return: -0.26, stocks: [{ symbol: 'SBUX', weight: 50, return_pct: -4.0 }, { symbol: 'NKE', weight: 50, return_pct: -3.0 }] },
  { display_name: 'SpaceCadet',     position_change:  0, total_return: -6.5, daily_avg_return: -0.27, stocks: [{ symbol: 'RKLB', weight: 60, return_pct: -8.0 }, { symbol: 'ASTS', weight: 40, return_pct: -5.0 }] },
  { display_name: 'WrongWayWendy',  position_change: -3, total_return: -6.9, daily_avg_return: -0.29, stocks: [{ symbol: 'INTC', weight: 70, return_pct: -16.0 }, { symbol: 'PARA', weight: 30, return_pct: -10.0 }] },
  { display_name: 'BioBlunder',     position_change:  1, total_return: -7.2, daily_avg_return: -0.30, stocks: [{ symbol: 'MRNA', weight: 50, return_pct: -12.0 }, { symbol: 'BNTX', weight: 50, return_pct: -8.0 }] },
  { display_name: 'LateToParty',    position_change:  0, total_return: -7.6, daily_avg_return: -0.32, stocks: [{ symbol: 'PTON', weight: 50, return_pct: -15.0 }, { symbol: 'ZM', weight: 50, return_pct: -12.0 }] },
  { display_name: 'BearMarketBob',  position_change: -1, total_return: -8.1, daily_avg_return: -0.34, stocks: [{ symbol: 'BYND', weight: 60, return_pct: -20.0 }, { symbol: 'WISH', weight: 40, return_pct: -18.0 }] },
]

// ── Derived data ───────────────────────────────────────────────────────────────

const entriesForDisplay = computed(() =>
  [...competition.entries]
    .sort((a, b) => a.rank - b.rank)
    .map((entry) => {
      const adjustedReturn = LEADER_TARGET_RETURNS[entry.rank] ?? entry.total_return
      const sharedStocks = OVERLAP_STOCKS_BY_RANK[entry.rank] ?? entry.stocks
      const totalReturn = Number(adjustedReturn.toFixed(1))
      const portfolioValue = Math.round(10000 * (1 + totalReturn / 100))
      const dailyAvgReturn = Number((totalReturn / Math.max(1, competition.days_elapsed)).toFixed(2))
      return {
        ...entry,
        stocks: sharedStocks,
        total_return: totalReturn,
        portfolio_value: portfolioValue,
        daily_avg_return: dailyAvgReturn,
      }
    })
)

const showAllPlayers = ref(false)

const fullTableRows = computed(() => {
  const rows = []
  entriesForDisplay.value.forEach((entry, i) => {
    const prev = entriesForDisplay.value[i - 1]
    if (prev && entry.rank > prev.rank + 1) {
      const gapCount = entry.rank - prev.rank - 1
      if (showAllPlayers.value) {
        FILLER_PLAYERS.slice(0, gapCount).forEach((filler, j) => {
          rows.push({ type: 'entry', rank: prev.rank + 1 + j, ...filler })
        })
      } else {
        rows.push({ type: 'gap', count: gapCount })
      }
    }
    rows.push({ type: 'entry', ...entry })
  })
  return rows
})

const universeStocks = computed(() => {
  const symbolMap = new Map()
  entriesForDisplay.value.forEach((entry) => {
    entry.stocks.forEach((stock) => {
      const existing = symbolMap.get(stock.symbol) ?? {
        symbol: stock.symbol,
        weightedReturn: 0,
        weightTotal: 0,
        pickCount: 0,
      }
      existing.weightedReturn += stock.return_pct * stock.weight
      existing.weightTotal += stock.weight
      existing.pickCount += 1
      symbolMap.set(stock.symbol, existing)
    })
  })
  return [...symbolMap.values()]
    .map((stock) => {
      const returnPct = stock.weightTotal > 0 ? stock.weightedReturn / stock.weightTotal : 0
      return {
        symbol: stock.symbol,
        returnPct: Number(returnPct.toFixed(2)),
        pickCount: stock.pickCount,
        exposure: stock.weightTotal / Math.max(1, entriesForDisplay.value.length),
      }
    })
    .sort((a, b) => b.returnPct - a.returnPct)
})

const topUniverseRankedEntries = computed(() => universeStocks.value.slice(0, 5))
const bottomUniverseEntry = computed(() => universeStocks.value[universeStocks.value.length - 1] ?? null)
const missingUniverseCount = computed(() => Math.max(0, universeStocks.value.length - 6))
const chartStockEntries = computed(() => universeStocks.value.slice(0, 5))

const topRankedEntries = computed(() => entriesForDisplay.value.slice(0, 5))
const topLeaderboardEntries = computed(() => topRankedEntries.value)
const bottomRankedEntry = computed(() => entriesForDisplay.value[entriesForDisplay.value.length - 1] ?? null)
const missingPlayersCount = computed(() => Math.max(0, competition.total_players - 6))
const hoveredLeaderboardEntry = computed(() =>
  entriesForDisplay.value.find((e) => e.display_name === hoveredPlayerName.value) ?? null
)

const tooltipStyle = computed(() => ({
  left: `${tooltipX.value}px`,
  top: `${tooltipY.value}px`,
}))

// ── Composables ────────────────────────────────────────────────────────────────

const { tradingDates } = useCompetitionDates(competition)

const activeSeriesName = computed(() =>
  rightPanelTab.value === 'stocks'
    ? (focusedStockSymbol.value ?? hoveredStockSymbol.value)
    : (focusedPlayerName.value ?? hoveredPlayerName.value)
)

const isFocused = computed(() =>
  rightPanelTab.value === 'stocks'
    ? focusedStockSymbol.value !== null
    : focusedPlayerName.value !== null
)

const { chartSeries, chartOptions } = useTradingChart({
  rightPanelTab,
  chartStockEntries,
  topRankedEntries,
  tradingDates,
  isDark,
  activeSeriesName,
  isFocused,
})

// ── Interaction handlers ───────────────────────────────────────────────────────

const handleTabChange = (tab) => { rightPanelTab.value = tab }

const handlePlayerHover = (event, displayName) => {
  hoveredPlayerName.value = displayName
  const offset = 14
  const margin = 8
  const tooltipWidth = 320
  const tooltipHeight = 250
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  const placements = [
    { name: 'bottom-start', left: event.clientX + offset, top: event.clientY + offset },
    { name: 'top-start',    left: event.clientX + offset, top: event.clientY - tooltipHeight - offset },
    { name: 'bottom-end',   left: event.clientX - tooltipWidth - offset, top: event.clientY + offset },
    { name: 'top-end',      left: event.clientX - tooltipWidth - offset, top: event.clientY - tooltipHeight - offset },
  ]

  const isVisible = (p) =>
    p.left >= margin &&
    p.top >= margin &&
    p.left + tooltipWidth <= viewportWidth - margin &&
    p.top + tooltipHeight <= viewportHeight - margin

  const best = placements.find(isVisible) ?? placements[0]
  tooltipPlacement.value = best.name
  tooltipX.value = Math.min(Math.max(best.left, margin), viewportWidth - tooltipWidth - margin)
  tooltipY.value = Math.min(Math.max(best.top, margin), viewportHeight - tooltipHeight - margin)
}

const clearPlayerHover = () => { hoveredPlayerName.value = null }
const handlePlayerClick = (displayName) => {
  focusedPlayerName.value = focusedPlayerName.value === displayName ? null : displayName
}

const handleStockHover = (symbol) => { hoveredStockSymbol.value = symbol }
const clearStockHover = () => { hoveredStockSymbol.value = null }
const handleStockClick = (symbol) => {
  focusedStockSymbol.value = focusedStockSymbol.value === symbol ? null : symbol
}
</script>
